// import './TenantCommunicationRentalPlanVariable.scss';
import {
  RentalAgreement,
  RentalAgreementTimePeriod,
} from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import DateFieldUtils from "@/modules/abstract-ui/forms/datefield/DateFieldUtils";
import { store } from "@/redux/store";
import CacheService from "@/services/CacheService";
import { queryAllAssetsChained } from "@/services/DataService";
import LanguageService from "@/services/LanguageService";
import DateUtils from "@/utils/DateUtils";
import MQ from "@/utils/MatchQueryUtils";
import StringUtils from "@/utils/StringUtils";
import _ from "lodash";
import moment from "moment";
import ReactDOMServer from "react-dom/server";

export const getRentalPlanVariable = async (
  rentalAgreement: RentalAgreement,
  year: number
) => {
  const cacheKey = `rentalPlan:${rentalAgreement._id}:${year}`;
  return CacheService.fetch(
    cacheKey,
    async () => {
      const from = moment().year(year).startOf("year").toDate();
      const to = moment().year(year).endOf("year").toDate();
      const allTimePeriods = await queryAllAssetsChained(
        store.dispatch,
        AssetTypes.Rental.RentalAgreementPeriod,
        MQ.and(
          MQ.ne("data.status", "archived"),
          MQ.eq("data.rentalAgreement", rentalAgreement._id)
          // MQ.or(
          //   // check that data.from and data.to are within the requested year
          //   MQ.and(
          //     MQ.lte("data.from", from),
          //     MQ.or(MQ.gte("data.to", from), MQ.isNull("data.to"))
          //   ),
          //   MQ.and(MQ.gte("data.from", from), MQ.lte("data.to", to))
          // )
        )
      );
      const filteredTimePeriods = allTimePeriods.filter((period) => {
        const periodFrom = moment(period.data.from);
        const periodTo = period.data.to ? moment(period.data.to) : null;

        const isWithinPeriod = DateUtils.isPeriodOverlapping(
          {
            from: from,
            to: to,
          },
          {
            from: periodFrom.toDate(),
            to: periodTo?.toDate(),
          }
        );

        return isWithinPeriod;
      });

      const portfolioObject = await CacheService.getData({
        oType: "asset",
        id: rentalAgreement.data.objectId,
        assetType: AssetTypes.Portfolio.Object,
        ignoreDelay: true,
      });
      const html = ReactDOMServer.renderToStaticMarkup(
        <TenantCommunicationRentalPlanVariable
          object={portfolioObject}
          rentalAgreement={rentalAgreement}
          timePeriods={filteredTimePeriods}
          from={from}
          to={to}
        />
      );

      return html;
    },
    1
  );
};

interface TenantCommunicationRentalPlanVariableProps {
  rentalAgreement: RentalAgreement;
  timePeriods: RentalAgreementTimePeriod[];
  from: Date;
  to: Date;
  object: OAObject;
}
const TenantCommunicationRentalPlanVariable = (
  props: TenantCommunicationRentalPlanVariableProps
) => {
  const sumsByMonth: {
    [date: string]: {
      [key: string]: { net: number; gross: number };
    };
  } = {};

  const firstMonth = moment(props.from).utc(true).startOf("month");
  const lastMonth = moment(props.to).utc(true).endOf("month");

  let generalStartDay = null;
  let generalEndDay = null;
  let currentMonth = firstMonth.clone().utc(true);
  while (currentMonth.isSameOrBefore(lastMonth)) {
    const monthsString = currentMonth.format("YYYY-MM");
    const matchingPeriods = props.timePeriods.filter((period) => {
      const periodFrom = moment(period.data.from).utc(true);
      const periodTo = period.data.to ? moment(period.data.to).utc(true) : null;

      const isWithinPeriod = DateUtils.isPeriodOverlapping(
        {
          from: currentMonth.clone().utc(true).startOf("month").toDate(),
          to: currentMonth.clone().utc(true).endOf("month").toDate(),
        },
        {
          from: periodFrom.toDate(),
          to: periodTo?.toDate(),
        }
      );
      return isWithinPeriod;
    });

    matchingPeriods.forEach((period) => {
      const startDayOfMonth = moment.max(
        moment(period.data.from).utc(true),
        currentMonth.clone().startOf("month")
      );
      const endDayOfMonth = !period.data.to
        ? currentMonth.clone().endOf("month")
        : moment.min(
            moment(period.data.to),
            currentMonth.clone().endOf("month")
          );
      generalEndDay = !generalEndDay
        ? endDayOfMonth
        : moment.max(endDayOfMonth);
      generalStartDay = !generalStartDay
        ? startDayOfMonth
        : moment.min(startDayOfMonth, generalStartDay);

      const days = endDayOfMonth.diff(startDayOfMonth, "days") + 1;
      const percentageUsedInMonth = days / currentMonth.daysInMonth();

      if (sumsByMonth[monthsString] === undefined) {
        sumsByMonth[monthsString] = {};
      }
      period.data.paymentPositions.forEach((position) => {
        if (!sumsByMonth[monthsString][position.id]) {
          sumsByMonth[monthsString][position.id] = { net: 0, gross: 0 };
        }
        sumsByMonth[monthsString][position.id].gross +=
          position.gross * percentageUsedInMonth;
        sumsByMonth[monthsString][position.id].net +=
          position.net * percentageUsedInMonth;
      });
    });

    currentMonth = currentMonth.add(1, "month");
  }

  const batches: {
    start: string;
    end: string;
    positions: {
      [key: string]: { net: number; gross: number };
    };
  }[] = [];

  Object.entries(sumsByMonth).forEach(([month, values]) => {
    let add = false;
    if (batches.length === 0) {
      add = true;
    } else {
      const lastbatch = batches[batches.length - 1];

      if (_.isEqual(lastbatch.positions, values)) {
        lastbatch.end = month;
      } else {
        add = true;
      }
    }

    if (add) {
      batches.push({
        start: month,
        end: month,
        positions: values,
      });
    }
  });

  return (
    <>
      <table
        style={{
          width: "100%",
        }}
      >
        <tr>
          <th
            style={{
              textAlign: "left",
              paddingTop: 5,
              paddingBottom: 5,
              borderTop: "solid 1px var(--border-color-contrast-inverse)",
              borderBottom: "solid 1px var(--border-color-contrast-inverse)",
              borderLeft: "none",
              borderRight: "none",
            }}
          >
            {i18n.t(
              "apTemplate:StartTenantCommunication.variables.rentalPlan.duration",
              "Leistungszeitraum"
            )}
          </th>
          <th
            colSpan={4}
            style={{
              textAlign: "left",
              paddingTop: 5,
              paddingBottom: 5,
              tableLayout: "auto",
              borderTop: "solid 1px var(--border-color-contrast-inverse)",
              borderBottom: "solid 1px var(--border-color-contrast-inverse)",
              borderLeft: "none",
              borderRight: "none",
            }}
          >
            {i18n.t(
              "apTemplate:StartTenantCommunication.variables.rentalPlan.remark",
              "Bemerkung"
            )}
          </th>
          <th
            style={{
              textAlign: "right",
              paddingTop: 5,
              paddingBottom: 5,
              borderTop: "solid 1px var(--border-color-contrast-inverse)",
              borderBottom: "solid 1px var(--border-color-contrast-inverse)",
              borderLeft: "none",
              borderRight: "none",
            }}
          >
            {i18n.t(
              "apTemplate:StartTenantCommunication.variables.rentalPlan.net",
              "Netto"
            ) +
              " " +
              StringUtils.getCurrencySymbol()}
          </th>
        </tr>
        {batches.map((batch) => {
          const sums = batch.positions;
          return (
            <>
              {Object.entries(sums).map(([id, value]) => {
                const paymentPosition =
                  props.object.data.feature.immo.accounting?.debitposition?.find(
                    (e) => e.id === id
                  );

                return (
                  <tr>
                    <td
                      style={{
                        textAlign: "left",
                        paddingTop: 5,
                        paddingBottom: 5,
                      }}
                    >
                      {moment(batch.start)
                        .utc(true)
                        .startOf("month")
                        .format(DateFieldUtils.getFormat("date"))}{" "}
                      -{" "}
                      {moment(batch.end)
                        .utc(true)
                        .endOf("month")
                        .format(DateFieldUtils.getFormat("date"))}
                    </td>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "left",
                        paddingTop: 5,
                        paddingBottom: 5,
                      }}
                    >
                      {LanguageService.translateLabel(
                        paymentPosition.displayName
                      ).replace("Ust. 19%", "")}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        paddingTop: 5,
                        paddingBottom: 5,
                      }}
                    >
                      {StringUtils.formatCurrency(value.net)}
                    </td>
                  </tr>
                );
              })}

              {props.rentalAgreement.data.taxable && (
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      paddingTop: 5,
                      paddingBottom: 2,
                    }}
                    colSpan={2}
                  ></th>
                  <th
                    style={{
                      textAlign: "left",
                      paddingTop: 5,
                      paddingBottom: 2,
                    }}
                  >
                    {i18n.t(
                      "apTemplate:StartTenantCommunication.variables.rentalPlan.netto",
                      "Netto"
                    )}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      paddingTop: 5,
                      paddingBottom: 2,
                    }}
                  >
                    {i18n.t(
                      "apTemplate:StartTenantCommunication.variables.rentalPlan.ust",
                      "USt."
                    )}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      paddingTop: 5,
                      paddingBottom: 2,
                    }}
                  >
                    {i18n.t(
                      "apTemplate:StartTenantCommunication.variables.rentalPlan.ustSum",
                      "USt. Betrag"
                    )}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      paddingTop: 5,
                      paddingBottom: 2,
                    }}
                  >
                    {i18n.t(
                      "apTemplate:StartTenantCommunication.variables.rentalPlan.brutto",
                      "Brutto"
                    )}
                  </th>
                </tr>
              )}
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    paddingTop: 2,
                    paddingBottom: 10,
                    borderTop: "solid 1px var(--border-color-contrast-inverse)",
                    // borderBottom: "solid 1px var(--border-color-contrast-inverse)",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                  colSpan={2}
                >
                  {batches.length === 1 ? (
                    i18n.t(
                      "apTemplate:StartTenantCommunication.variables.rentalPlan.total",
                      "Gesamtsumme"
                    )
                  ) : (
                    <>
                      {moment(batch.start)
                        .utc(true)
                        .startOf("month")
                        .format(DateFieldUtils.getFormat("date"))}{" "}
                      -{" "}
                      {moment(batch.end)
                        .utc(true)
                        .endOf("month")
                        .format(DateFieldUtils.getFormat("date"))}
                    </>
                  )}
                </th>
                <th
                  style={{
                    textAlign: "left",
                    paddingTop: 2,
                    paddingBottom: 10,
                    borderTop: "solid 1px var(--border-color-contrast-inverse)",
                    // borderBottom: "solid 1px var(--border-color-contrast-inverse)",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  {props.rentalAgreement.data.taxable
                    ? StringUtils.formatCurrency(
                        Object.values(sums).reduce((acc, e) => acc + e.net, 0)
                      )
                    : ""}
                </th>
                <th
                  style={{
                    textAlign: "left",
                    paddingTop: 2,
                    paddingBottom: 10,
                    borderTop: "solid 1px var(--border-color-contrast-inverse)",
                    // borderBottom: "solid 1px var(--border-color-contrast-inverse)",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  {props.rentalAgreement.data.taxable ? "19%" : ""}
                </th>
                <th
                  style={{
                    textAlign: "left",
                    paddingTop: 2,
                    paddingBottom: 10,
                    borderTop: "solid 1px var(--border-color-contrast-inverse)",
                    // borderBottom: "solid 1px var(--border-color-contrast-inverse)",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  {props.rentalAgreement.data.taxable
                    ? StringUtils.formatCurrency(
                        _.sum(Object.values(sums).map((e) => e.gross - e.net))
                      )
                    : ""}
                </th>
                <th
                  style={{
                    textAlign: "right",
                    paddingTop: 2,
                    paddingBottom: 10,
                    borderTop: "solid 1px var(--border-color-contrast-inverse)",
                    // borderBottom: "solid 1px var(--border-color-contrast-inverse)",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  {props.rentalAgreement.data.taxable
                    ? StringUtils.formatCurrency(
                        Object.values(sums).reduce((acc, e) => acc + e.gross, 0)
                      )
                    : StringUtils.formatCurrency(
                        Object.values(sums).reduce((acc, e) => acc + e.net, 0)
                      )}
                </th>
              </tr>
            </>
          );
        })}
      </table>
    </>
  );
};

export default TenantCommunicationRentalPlanVariable;
